import React, { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Search, Trash2, Loader2 } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/* Pemilih produk MANUAL — cari via GET /products?search= (katalog existing), simpan hanya `id` (maks 8).
   Produk yang sudah tidak ada/nonaktif ditandai dan dilewati storefront secara otomatis. */
export function ProductPicker({ ids = [], onChange, max = 8 }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [byId, setById] = useState({});

  useEffect(() => {
    const missing = ids.filter((id) => !byId[id]);
    if (!missing.length) return undefined;
    let alive = true;
    Promise.all(missing.map((id) => api.get(`/products/${id}`).then((r) => [id, r.data]).catch(() => [id, null])))
      .then((pairs) => alive && setById((s) => ({ ...s, ...Object.fromEntries(pairs) })));
    return () => { alive = false; };
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setSearching(true);
    try { setResults(((await api.get("/products", { params: { search: q.trim(), limit: 10 } })).data?.items || [])); } catch (_) { setResults([]); } finally { setSearching(false); }
  };
  const add = (p) => { if (ids.length >= max || ids.includes(p.id)) return; setById((s) => ({ ...s, [p.id]: p })); onChange([...ids, p.id]); };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= ids.length) return; const n = [...ids]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };

  return (
    <div className="space-y-3" data-testid="cp-product-picker">
      <form onSubmit={search} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk…" data-testid="cp-product-search-input" />
        <Button type="submit" variant="outline" disabled={searching} data-testid="cp-product-search-btn">{searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}</Button>
      </form>
      {results.length > 0 ? (
        <ul className="rounded-xl border border-[#E5E5E5] divide-y max-h-56 overflow-auto" data-testid="cp-product-results">
          {results.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              {p.image ? <img src={imageUrl(p.image)} alt="" className="h-8 w-8 rounded object-cover" /> : <span className="h-8 w-8 rounded bg-[#F3F4F6]" />}
              <span className="flex-1 min-w-0 truncate">{p.name}</span>
              <Button type="button" size="sm" variant="ghost" disabled={ids.includes(p.id) || ids.length >= max} onClick={() => add(p)} data-testid={`cp-product-add-${p.id}`}><Plus size={14} /> Pilih</Button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="text-xs font-semibold text-[#374151]">Dipilih ({ids.length}/{max})</div>
      {ids.length === 0 ? <p className="text-xs text-[#8A8A8A]" data-testid="cp-product-selected-empty">Belum ada produk dipilih — storefront akan menampilkan section tanpa produk.</p> : null}
      <ol className="space-y-1.5" data-testid="cp-product-selected">
        {ids.map((id, i) => {
          const p = byId[id];
          const gone = p === null;
          return (
            <li key={id} className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${gone ? "border-amber-300 bg-amber-50" : "border-[#E5E5E5]"}`} data-testid={`cp-product-selected-${id}`}>
              <span className="text-xs text-[#8A8A8A] w-4">{i + 1}.</span>
              <span className="flex-1 min-w-0 truncate">{p?.name || (gone ? "Produk tidak tersedia (dilewati di storefront)" : "Memuat…")}</span>
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Naik" data-testid={`cp-product-up-${id}`}><ArrowUp size={13} /></Button>
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" disabled={i === ids.length - 1} onClick={() => move(i, 1)} aria-label="Turun" data-testid={`cp-product-down-${id}`}><ArrowDown size={13} /></Button>
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-[#DC2F02]" onClick={() => onChange(ids.filter((x) => x !== id))} aria-label="Hapus" data-testid={`cp-product-remove-${id}`}><Trash2 size={13} /></Button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
